import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { FileText, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { messaging, mk, type SmsTemplate, templatesQuery } from "../../../api/messaging";
import { useCanEdit } from "../../../auth/useMe";
import { DataTable } from "../../../components/DataTable";
import { TemplateEditor } from "../../../components/messaging/TemplateEditor";
import { toast } from "../../../components/toast";
import { Button, Card, EmptyState, ErrorState, Loading } from "../../../components/ui";
import { relTime } from "../../../lib/format";
import { TEMPLATE_VARIABLES } from "../../../lib/messaging";

export const Route = createFileRoute("/admin/messages/templates")({
  staticData: { crumb: { label: "Templates", parents: [] } },
  component: TemplatesPage,
});

const col = createColumnHelper<SmsTemplate>();

function TemplatesPage() {
  const qc = useQueryClient();
  const canEdit = useCanEdit();
  const q = useQuery(templatesQuery);
  // null: closed; "new": creating; otherwise the template being edited.
  const [editing, setEditing] = useState<SmsTemplate | "new" | null>(null);
  const variables = q.data?.variables ?? [...TEMPLATE_VARIABLES];

  const columns = useMemo(
    () => [
      col.accessor("name", {
        header: "Name",
        cell: (c) => <span className="font-medium">{c.getValue()}</span>,
      }),
      col.accessor("body", {
        header: "Message",
        enableSorting: false,
        cell: (c) => <span className="block max-w-[36rem] truncate text-ink-2">{c.getValue()}</span>,
      }),
      col.accessor("updated_at", {
        header: "Updated",
        cell: (c) => <span className="whitespace-nowrap text-ink-2">{relTime(c.getValue())}</span>,
      }),
    ],
    [],
  );

  const done = (text: string) => {
    setEditing(null);
    qc.invalidateQueries({ queryKey: mk.templates });
    toast(text);
  };

  return (
    <>
      {canEdit ? (
        <div className="mb-3 flex justify-end">
          <Button variant="primary" onClick={() => setEditing("new")}>
            <Plus />
            New template
          </Button>
        </div>
      ) : null}
      <Card>
        {q.isPending ? (
          <Loading />
        ) : q.isError ? (
          <ErrorState error={q.error} onRetry={() => q.refetch()} />
        ) : (
          <DataTable
            data={q.data.items}
            columns={columns}
            getRowId={(t) => t.id}
            initialSorting={[{ id: "name", desc: false }]}
            onRowClick={canEdit ? (t) => t.editable && setEditing(t) : undefined}
            rowLabel={(t) => `Edit ${t.name}`}
            empty={<EmptyState icon={<FileText />} title="No shared templates yet" />}
          />
        )}
      </Card>
      {editing ? (
        <TemplateEditor
          key={editing === "new" ? "new" : editing.id}
          open
          initial={editing === "new" ? null : { name: editing.name, body: editing.body }}
          variables={variables}
          onClose={() => setEditing(null)}
          onSave={async (d) => {
            if (editing === "new") {
              await messaging.createTemplate(d);
              done("Template added.");
            } else {
              await messaging.updateTemplate(editing.id, d);
              done("Template saved.");
            }
          }}
          onDelete={
            editing === "new"
              ? undefined
              : async () => {
                  await messaging.deleteTemplate(editing.id);
                  done("Template deleted.");
                }
          }
        />
      ) : null}
    </>
  );
}
